import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { ArrowUpRight } from "lucide-react";

export interface PortfolioAnchorLink {
  label: React.ReactNode;
  href: string;
}

export interface PortfolioAction {
  label: React.ReactNode;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

export interface PortfolioProject {
  id: string;
  client: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  category: string;
  categoryLabel?: React.ReactNode;
  tags?: string[];
  platformLinks?: Array<{
    label: React.ReactNode;
    href: string;
  }>;
  primaryLink?: {
    label: React.ReactNode;
    href: string;
  };
  results?: React.ReactNode;
  imageUrl?: string;
  imageAlt?: string;
}

export interface PortfolioStat {
  value: React.ReactNode;
  label: React.ReactNode;
}

export interface PortfolioCategory {
  key: string;
  title: React.ReactNode;
  description: React.ReactNode;
  icon?: React.ReactNode;
}

export interface PortfolioHeading {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
}

export interface PortfolioProcessStep {
  step: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
}

export interface PortfolioTestimonial {
  quote: React.ReactNode;
  author: React.ReactNode;
  role: React.ReactNode;
}

export interface PortfolioPageProps {
  hero: {
    eyebrow?: React.ReactNode;
    titleLine1: React.ReactNode;
    titleLine2Gradient: React.ReactNode;
    subtitle: React.ReactNode;
  };
  anchorLinks?: PortfolioAnchorLink[];
  ctaButtons: {
    primary: PortfolioAction;
    secondary?: PortfolioAction;
  };
  stats: PortfolioStat[];
  categories?: PortfolioCategory[];
  categoriesHeading?: PortfolioHeading;
  activeCategory?: string;
  onCategoryChange?: (category: string) => void;
  projectsHeading?: PortfolioHeading;
  projects: PortfolioProject[];
  loadingLabel?: React.ReactNode;
  resultsLabel?: React.ReactNode;
  processHeading?: PortfolioHeading;
  process?: PortfolioProcessStep[];
  testimonialHeading?: PortfolioHeading;
  testimonial?: PortfolioTestimonial;
  closingCta?: PortfolioHeading & {
    primary: PortfolioAction;
    secondary?: PortfolioAction;
  };
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  showAnimatedBackground?: boolean;
}

const AuroraBackground: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;

    if (!mount) {
      return;
    }

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    const material = new THREE.ShaderMaterial({
      transparent: true,
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      },
      vertexShader: "void main() { gl_Position = vec4(position, 1.0); }",
      fragmentShader: `
        uniform float iTime;
        uniform vec2 iResolution;
        #define NUM_OCTAVES 3

        float rand(vec2 n) {
          return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
        }

        float noise(vec2 p) {
          vec2 ip = floor(p);
          vec2 u = fract(p);
          u = u * u * (3.0 - 2.0 * u);
          float res = mix(
            mix(rand(ip), rand(ip + vec2(1.0, 0.0)), u.x),
            mix(rand(ip + vec2(0.0, 1.0)), rand(ip + vec2(1.0, 1.0)), u.x),
            u.y
          );
          return res * res;
        }

        float fbm(vec2 x) {
          float v = 0.0;
          float a = 0.3;
          vec2 shift = vec2(100.0);
          mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
          for (int i = 0; i < NUM_OCTAVES; ++i) {
            v += a * noise(x);
            x = rot * x * 2.0 + shift;
            a *= 0.4;
          }
          return v;
        }

        void main() {
          vec2 p = ((gl_FragCoord.xy) - iResolution.xy * 0.5) / iResolution.y * mat2(6.0, -4.0, 4.0, 6.0);
          vec4 o = vec4(0.0);
          float f = 2.0 + fbm(p + vec2(iTime * 5.0, 0.0)) * 0.5;

          for (float i = 1.0; i < 35.0; i++) {
            vec2 v = p + cos(i * i + (iTime + p.x * 0.08) * 0.025 + i * vec2(13.0, 11.0)) * 3.5;
            float tailNoise = fbm(v + vec2(iTime * 0.5, i)) * 0.3 * (1.0 - (i / 35.0));
            vec4 auroraColors = vec4(
              0.22 + 0.25 * sin(i * 0.2 + iTime * 0.4),
              0.34 + 0.35 * cos(i * 0.3 + iTime * 0.5),
              0.60 + 0.22 * sin(i * 0.4 + iTime * 0.3),
              0.75
            );
            vec4 currentContribution = auroraColors * exp(sin(i * i + iTime * 0.8)) / length(max(v, vec2(v.x * f * 0.015, v.y * 1.5)));
            float thinnessFactor = smoothstep(0.0, 1.0, i / 35.0) * 0.6;
            o += currentContribution * (1.0 + tailNoise * 0.8) * thinnessFactor;
          }

          o = tanh(pow(o / 100.0, vec4(1.6)));
          gl_FragColor = o * 1.4;
        }
      `,
    });
    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    let animationFrameId = 0;

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.domElement.style.position = "fixed";
    renderer.domElement.style.inset = "0";
    renderer.domElement.style.zIndex = "0";
    renderer.domElement.style.pointerEvents = "none";
    renderer.domElement.style.opacity = "0.9";

    scene.add(mesh);
    mount.appendChild(renderer.domElement);

    const handleResize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
      material.uniforms.iResolution.value.set(window.innerWidth, window.innerHeight);
    };

    const animate = () => {
      material.uniforms.iTime.value += 0.012;
      renderer.render(scene, camera);
      animationFrameId = window.requestAnimationFrame(animate);
    };

    window.addEventListener("resize", handleResize);
    animate();

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" />;
};

const ActionButton = ({
  action,
  variant = "primary",
}: {
  action?: PortfolioAction;
  variant?: "primary" | "secondary";
}) => {
  if (!action) {
    return null;
  }

  const className =
    variant === "primary"
      ? "portfolio-primary-action"
      : "portfolio-secondary-action";

  const content = (
    <>
      <span>{action.label}</span>
      {action.icon ?? (variant === "primary" ? <ArrowUpRight className="h-4 w-4" /> : null)}
    </>
  );

  if (action.href) {
    return (
      <a href={action.href} className={className}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={action.onClick} className={className}>
      {content}
    </button>
  );
};

const SectionHeading = ({ eyebrow, title, description }: PortfolioHeading) => (
  <div className="max-w-3xl space-y-4">
    {eyebrow ? <p className="portfolio-eyebrow">{eyebrow}</p> : null}
    <h2 className="font-poppins text-3xl font-black tracking-tight text-[#233559] dark:text-white md:text-4xl">
      {title}
    </h2>
    {description ? (
      <p className="max-w-2xl font-source-sans text-base leading-7 text-foreground/70">
        {description}
      </p>
    ) : null}
  </div>
);

export const PortfolioPage: React.FC<PortfolioPageProps> = ({
  hero,
  anchorLinks = [],
  ctaButtons,
  stats,
  categories = [],
  categoriesHeading,
  activeCategory = "all",
  onCategoryChange,
  projectsHeading,
  projects,
  loadingLabel = "Loading portfolio stories",
  resultsLabel = "Results / impact",
  processHeading,
  process = [],
  testimonialHeading,
  testimonial,
  closingCta,
  loading = false,
  emptyMessage,
  showAnimatedBackground = true,
}) => {
  return (
    <div className="portfolio-shell relative overflow-hidden bg-background text-foreground">
      {showAnimatedBackground ? <AuroraBackground /> : null}

      <div className="relative z-10">
        <main className="px-4 pb-20 pt-28 sm:px-6 lg:px-8 lg:pt-32">
          <div className="mx-auto max-w-7xl space-y-10">
            <section className="portfolio-panel overflow-hidden">
              <div className="grid gap-10 px-6 py-8 md:px-10 md:py-10 lg:grid-cols-[1.35fr_0.9fr] lg:items-end">
                <div className="space-y-8">
                  <div className="space-y-5">
                    {hero.eyebrow ? <p className="portfolio-eyebrow">{hero.eyebrow}</p> : null}
                    <div className="space-y-4">
                      <h1 className="max-w-4xl font-poppins text-5xl font-black leading-[0.98] tracking-[-0.04em] text-[#233559] dark:text-white md:text-6xl lg:text-7xl">
                        <span className="block">{hero.titleLine1}</span>
                        <span className="portfolio-gradient-text block">{hero.titleLine2Gradient}</span>
                      </h1>
                      <p className="max-w-2xl font-source-sans text-lg leading-8 text-foreground/75 md:text-xl">
                        {hero.subtitle}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <ActionButton action={ctaButtons.primary} variant="primary" />
                    <ActionButton action={ctaButtons.secondary} variant="secondary" />
                  </div>

                  {anchorLinks.length > 0 ? (
                    <div className="flex flex-wrap gap-3">
                      {anchorLinks.map((link) => (
                        <a key={link.href} href={link.href} className="portfolio-chip">
                          {link.label}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
                  {stats.map((stat) => (
                    <div key={`${stat.value}-${stat.label}`} className="portfolio-stat-card">
                      <div className="font-poppins text-3xl font-black tracking-tight text-[#233559] dark:text-white md:text-4xl">
                        {stat.value}
                      </div>
                      <p className="font-source-sans text-sm uppercase tracking-[0.22em] text-foreground/55">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {categoriesHeading ? (
              <section id="portfolio-capabilities" className="space-y-8">
                <SectionHeading {...categoriesHeading} />
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {categories.map((category) => {
                    const isActive = category.key === activeCategory;

                    return (
                      <button
                        key={category.key}
                        type="button"
                        onClick={() => onCategoryChange?.(category.key)}
                        className={`portfolio-category-card text-left ${isActive ? "portfolio-category-card-active" : ""}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className="portfolio-icon-badge">{category.icon}</span>
                            <h3 className="font-poppins text-lg font-bold text-[#233559] dark:text-white">
                              {category.title}
                            </h3>
                          </div>
                          <ArrowUpRight className="h-4 w-4 text-[#7D468C]" />
                        </div>
                        <p className="mt-4 font-source-sans text-sm leading-6 text-foreground/70">
                          {category.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <section id="portfolio-work" className="space-y-8">
              {projectsHeading ? <SectionHeading {...projectsHeading} /> : null}

              {loading ? (
                <div className="portfolio-panel px-6 py-16 text-center md:px-10">
                  <div className="portfolio-loader mx-auto" />
                  <p className="mt-4 font-source-sans text-sm uppercase tracking-[0.24em] text-foreground/55">
                    {loadingLabel}
                  </p>
                </div>
              ) : projects.length === 0 ? (
                <div className="portfolio-panel px-6 py-16 text-center md:px-10">
                  <p className="font-source-sans text-lg leading-7 text-foreground/70">
                    {emptyMessage}
                  </p>
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {projects.map((project) => (
                    <article key={project.id} className="group portfolio-project-card">
                      <div className="relative h-60 overflow-hidden border-b border-black/8 dark:border-white/8">
                        {project.imageUrl ? (
                          <img
                            src={project.imageUrl}
                            alt={project.imageAlt || ""}
                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        ) : (
                          <div className="h-full w-full bg-gradient-to-br from-[#7D468C]/20 via-[#4EBFBF]/10 to-[#F2785C]/20" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1C2E]/72 via-transparent to-transparent" />
                        <div className="absolute left-4 top-4">
                          <span className="portfolio-tag">
                            {project.categoryLabel || project.category}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-5 p-6">
                        <div className="space-y-2">
                          <p className="font-source-sans text-sm uppercase tracking-[0.22em] text-[#7D468C] dark:text-[#B582C3]">
                            {project.client}
                          </p>
                          <h3 className="font-poppins text-2xl font-bold leading-tight text-[#233559] dark:text-white">
                            {project.title}
                          </h3>
                          <p className="font-source-sans text-base leading-7 text-foreground/75">
                            {project.description}
                          </p>
                        </div>

                        {project.results ? (
                          <div className="rounded-2xl border border-black/8 bg-white/75 p-4 dark:border-white/8 dark:bg-white/5">
                            <p className="font-source-sans text-xs font-semibold uppercase tracking-[0.2em] text-foreground/45">
                              {resultsLabel}
                            </p>
                            <p className="mt-2 font-source-sans text-sm leading-6 text-foreground/75">
                              {project.results}
                            </p>
                          </div>
                        ) : null}

                        {project.tags?.length ? (
                          <div className="flex flex-wrap gap-2">
                            {project.tags.map((tag) => (
                              <span key={tag} className="portfolio-tag-muted">
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : null}

                        {project.platformLinks?.length ? (
                          <div className="flex flex-wrap gap-2">
                            {project.platformLinks.map((link) => (
                              <a
                                key={`${project.id}-${link.href}`}
                                href={link.href}
                                target="_blank"
                                rel="noreferrer"
                                className="portfolio-tag inline-flex items-center transition-opacity hover:opacity-80"
                              >
                                {link.label}
                              </a>
                            ))}
                          </div>
                        ) : null}

                        {project.primaryLink ? (
                          <div>
                            <a
                              href={project.primaryLink.href}
                              className="inline-flex items-center gap-2 rounded-full bg-[#233559] px-4 py-2.5 font-poppins text-sm font-semibold text-white transition-colors hover:bg-[#7D468C]"
                            >
                              <span>{project.primaryLink.label}</span>
                              <ArrowUpRight className="h-4 w-4" />
                            </a>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            {process.length > 0 ? (
              <section id="portfolio-process" className="space-y-8">
                {processHeading ? <SectionHeading {...processHeading} /> : null}
                <div className="grid gap-5 lg:grid-cols-3">
                  {process.map((item) => (
                    <article key={`${item.step}-${item.title}`} className="portfolio-process-card">
                      <p className="font-poppins text-sm font-black tracking-[0.24em] text-[#7D468C]">
                        {item.step}
                      </p>
                      <h3 className="mt-5 font-poppins text-2xl font-bold text-[#233559] dark:text-white">
                        {item.title}
                      </h3>
                      <p className="mt-4 font-source-sans text-base leading-7 text-foreground/70">
                        {item.description}
                      </p>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}

            {testimonial ? (
              <section className="portfolio-panel overflow-hidden px-6 py-10 md:px-10">
                <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                  {testimonialHeading ? <SectionHeading {...testimonialHeading} /> : <div />}
                  <div className="space-y-5">
                    <p className="font-source-sans text-xl leading-9 text-foreground/80">
                      "{testimonial.quote}"
                    </p>
                    <div>
                      <p className="font-poppins text-base font-bold text-[#233559] dark:text-white">{testimonial.author}</p>
                      <p className="font-source-sans text-sm uppercase tracking-[0.2em] text-foreground/50">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            ) : null}

            {closingCta ? (
              <section id="portfolio-cta" className="portfolio-cta-panel">
                <div className="max-w-3xl space-y-5">
                  {closingCta.eyebrow ? <p className="portfolio-eyebrow text-white/70">{closingCta.eyebrow}</p> : null}
                  <h2 className="font-poppins text-3xl font-black tracking-tight text-white md:text-5xl">
                    {closingCta.title}
                  </h2>
                  {closingCta.description ? (
                    <p className="font-source-sans text-lg leading-8 text-white/74">
                      {closingCta.description}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <ActionButton action={closingCta.primary} variant="primary" />
                  <ActionButton action={closingCta.secondary} variant="secondary" />
                </div>
              </section>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
};
